import React from "react";
import {
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  Plane,
  Hotel,
  Phone,
  User,
  CalendarClock,
  CreditCard,
  Sparkles,
  MapPin,
  Clock,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Semaphore } from "@/components/Semaphore";
import { PaymentBadges } from "@/components/PaymentBadge";
import { STATUS_META, formatMoney, formatDate } from "@/lib/constants";

// Helper to assign a stable, rich avatar gradient per client name
const getAvatarGradient = (name = "") => {
  const gradients = [
    "from-slate-700 to-slate-900",
    "from-indigo-600 to-blue-700",
    "from-teal-600 to-emerald-700",
    "from-violet-600 to-purple-800",
    "from-cyan-600 to-blue-700",
    "from-rose-600 to-pink-700",
    "from-amber-600 to-orange-700",
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % gradients.length;
  return gradients[index];
};

// Helper for travel urgency breakdown
const getTravelUrgency = (reservation) => {
  if (reservation?.travel_date_open) {
    return {
      label: "Fecha abierta",
      badgeClass: "bg-amber-50 text-amber-800 border-amber-200/80",
      dotClass: "bg-amber-500",
      isOpen: true,
    };
  }
  const dateStr = reservation?.travel_date;
  if (!dateStr) {
    return {
      label: "Sin fecha asignada",
      badgeClass: "bg-slate-100 text-slate-500 border-slate-200",
      dotClass: "bg-slate-400",
      isUnset: true,
    };
  }

  const target = new Date(dateStr);
  if (isNaN(target.getTime())) {
    return {
      label: dateStr,
      badgeClass: "bg-slate-100 text-slate-600 border-slate-200",
      dotClass: "bg-slate-400",
    };
  }

  const now = new Date();
  target.setHours(0, 0, 0, 0);
  now.setHours(0, 0, 0, 0);

  const diffMs = target.getTime() - now.getTime();
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) {
    return {
      label: "Viaje completado",
      badgeClass: "bg-slate-100 text-slate-600 border-slate-200",
      dotClass: "bg-slate-400",
      isPast: true,
    };
  }
  if (diffDays === 0) {
    return {
      label: "¡Viaja hoy!",
      badgeClass: "bg-rose-50 text-rose-700 border-rose-200 font-bold",
      dotClass: "bg-rose-500 animate-ping",
      isUrgent: true,
    };
  }
  if (diffDays === 1) {
    return {
      label: "Sale mañana",
      badgeClass: "bg-amber-50 text-amber-800 border-amber-200 font-semibold",
      dotClass: "bg-amber-500",
      isUrgent: true,
    };
  }
  if (diffDays <= 7) {
    return {
      label: `En ${diffDays} días`,
      badgeClass: "bg-sky-50 text-sky-700 border-sky-200 font-semibold",
      dotClass: "bg-sky-500",
      isUrgent: true,
    };
  }
  return {
    label: formatDate(dateStr),
    badgeClass: "bg-slate-100 text-slate-700 border-slate-200",
    dotClass: "bg-slate-400",
  };
};

export const ClientCardsView = ({
  clients = [],
  labels = {},
  currency = "USD",
  convertAmount = (v) => v,
  onChangeStatus,
  onSelectClient,
  onEditClient,
  onReservationClient,
  onDeleteClient,
  loading = false,
}) => {
  // Skeleton Loading State
  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
        {[1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={i}
            className="rounded-2xl border border-slate-200/80 bg-white p-5 animate-pulse flex flex-col justify-between space-y-4 shadow-2xs"
          >
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <div className="h-6 w-24 bg-slate-200 rounded-md" />
                <div className="h-6 w-20 bg-slate-200 rounded-full" />
              </div>
              <div className="flex items-center gap-3 pt-2">
                <div className="h-11 w-11 rounded-xl bg-slate-200 shrink-0" />
                <div className="space-y-1.5 flex-1">
                  <div className="h-5 w-3/4 bg-slate-200 rounded" />
                  <div className="h-3 w-1/2 bg-slate-100 rounded" />
                </div>
              </div>
            </div>
            <div className="h-20 bg-slate-100 rounded-xl" />
            <div className="space-y-2 pt-2 border-t border-slate-100">
              <div className="h-4 w-full bg-slate-100 rounded" />
              <div className="h-9 w-full bg-slate-200 rounded-lg" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Zero-State Empty View
  if (clients.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center shadow-2xs">
        <div className="h-14 w-14 rounded-2xl bg-slate-100 text-slate-400 mx-auto flex items-center justify-center mb-3">
          <Plane className="h-7 w-7 text-slate-400" />
        </div>
        <h3 className="font-heading font-bold text-slate-800 text-lg">
          No se encontraron expedientes
        </h3>
        <p className="text-sm text-slate-500 mt-1 max-w-sm mx-auto">
          No hay clientes que coincidan con los filtros aplicados o el término de búsqueda.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 2xl:grid-cols-4 gap-5">
      {clients.map((c) => {
        // Financial conversions
        const paidUsd = (c.payments || []).reduce((s, p) => s + (Number(p.amount) || 0), 0);
        const totalUsd = Number(c.total_amount) || 0;
        const balanceUsd = Math.max(0, totalUsd - paidUsd);

        const paidConv = convertAmount(paidUsd);
        const totalConv = convertAmount(totalUsd);
        const balanceConv = convertAmount(balanceUsd);

        const percentPaid = totalUsd > 0 ? Math.min(100, Math.round((paidUsd / totalUsd) * 100)) : 100;
        const isLiquidated = balanceConv <= 0.01;

        // Reservation & Travel metadata
        const travelUrgency = getTravelUrgency(c.reservation);
        const hasFlights = Boolean(c.reservation?.has_flights);

        // Client monogram initials & avatar gradient
        const initials =
          (c.full_name || "")
            .split(" ")
            .filter(Boolean)
            .map((w) => w[0])
            .slice(0, 2)
            .join("")
            .toUpperCase() || "CL";

        const avatarGradient = getAvatarGradient(c.full_name || "");
        const statusMeta = STATUS_META[c.status] || STATUS_META.gris;

        return (
          <div
            key={c.id}
            data-testid={`client-card-${c.id}`}
            className="group relative rounded-2xl border border-slate-200/90 bg-white shadow-2xs hover:shadow-lg hover:border-slate-300 hover:-translate-y-0.5 transition-all duration-200 flex flex-col justify-between overflow-hidden"
          >
            {/* Top Status Accent Bar */}
            <div
              className="h-1 w-full"
              style={{
                backgroundColor: statusMeta.hex,
              }}
            />

            <div className="p-5 flex-1 flex flex-col justify-between space-y-4">
              {/* Header: Code + Interactive Status Semaphore + Menu */}
              <div>
                <div className="flex items-center justify-between gap-2 mb-3.5">
                  <span
                    className="font-mono text-xs font-bold text-slate-700 bg-slate-100/90 border border-slate-200/80 px-2.5 py-0.5 rounded-lg tracking-wider"
                    title={`Código de expediente: ${c.client_code}`}
                  >
                    #{c.client_code}
                  </span>

                  <div className="flex items-center gap-1.5">
                    {/* Interactive Semaphore Status Selector */}
                    <Semaphore
                      status={c.status}
                      onChange={(newStatus) => onChangeStatus?.(c.id, newStatus)}
                      labels={labels}
                      size="sm"
                    />

                    {/* Quick Card Action Dropdown */}
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-slate-400 hover:text-slate-800 -mr-1"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-52">
                        <DropdownMenuItem onClick={() => onSelectClient?.(c)}>
                          <Eye className="h-4 w-4 mr-2 text-slate-500" /> Ver Ficha Completa
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onReservationClient?.(c)}>
                          <Plane className="h-4 w-4 mr-2 text-slate-500" /> Gestionar Reserva
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => onEditClient?.(c)}>
                          <Pencil className="h-4 w-4 mr-2 text-slate-500" /> Editar Cliente
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-600 focus:bg-red-50"
                          onClick={() => onDeleteClient?.(c)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" /> Eliminar Expediente
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>

                {/* Client Hero: Avatar + Name + Advisor + Phone */}
                <div className="flex items-start gap-3">
                  <div
                    onClick={() => onSelectClient?.(c)}
                    className={`h-11 w-11 rounded-xl bg-gradient-to-br ${avatarGradient} text-white flex items-center justify-center text-xs font-bold shrink-0 shadow-xs cursor-pointer group-hover:scale-105 transition-transform`}
                  >
                    {initials}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3
                      onClick={() => onSelectClient?.(c)}
                      className="font-heading font-bold text-slate-900 text-base leading-snug truncate cursor-pointer hover:text-blue-600 transition-colors"
                      title={c.full_name}
                    >
                      {c.full_name}
                    </h3>
                    <div className="flex flex-wrap items-center gap-2 mt-1">
                      {c.assigned_to && (
                        <span className="inline-flex items-center text-[11px] font-medium text-slate-600 bg-slate-100/80 border border-slate-200/60 px-2 py-0.5 rounded-md">
                          <User className="h-3 w-3 mr-1 text-slate-400" />
                          <span className="truncate max-w-[120px]">{c.assigned_to}</span>
                        </span>
                      )}
                      {c.phone && (
                        <a
                          href={`tel:${c.phone}`}
                          onClick={(e) => e.stopPropagation()}
                          className="inline-flex items-center text-[11px] text-slate-500 hover:text-slate-900 transition-colors font-mono"
                          title={c.phone}
                        >
                          <Phone className="h-3 w-3 mr-1 text-slate-400" />
                          <span className="truncate max-w-[110px]">{c.phone}</span>
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Destination & Itinerary Context Box */}
              <div className="rounded-xl bg-slate-50/80 border border-slate-200/70 p-3 space-y-2 text-xs">
                {/* Destination / Hotel */}
                <div className="flex items-center gap-2 text-slate-800">
                  <MapPin className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                  <span className="truncate font-semibold text-slate-900" title={c.hotel || "Destino pendiente"}>
                    {c.hotel || "Destino / Hotel pendiente"}
                  </span>
                </div>

                {/* Departure & Proximity */}
                <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-slate-200/50">
                  <div className="flex items-center gap-1.5 text-slate-500">
                    <CalendarClock className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                    <span>Salida:</span>
                  </div>
                  <span
                    className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] border ${travelUrgency.badgeClass}`}
                  >
                    <span className={`h-1.5 w-1.5 rounded-full ${travelUrgency.dotClass}`} />
                    {travelUrgency.label}
                  </span>
                </div>

                {/* Flights & Logistics */}
                <div className="flex items-center justify-between gap-2 pt-1.5 border-t border-slate-200/50 text-[11px]">
                  <span className="text-slate-500">Logística de vuelo:</span>
                  {hasFlights ? (
                    <span className="inline-flex items-center font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-md border border-emerald-200/60">
                      <Plane className="h-3 w-3 mr-1 text-emerald-600" /> Con vuelos
                    </span>
                  ) : (
                    <span className="text-slate-400 font-medium">Solo hotel / terrestre</span>
                  )}
                </div>
              </div>

              {/* Financial Health Hub */}
              <div className="space-y-2 pt-1">
                {/* Total vs Saldo */}
                <div className="flex items-baseline justify-between">
                  <div className="text-xs font-semibold text-slate-500">Total Expediente</div>
                  <div className="font-mono text-base font-extrabold text-slate-900">
                    {formatMoney(totalConv, currency)}
                  </div>
                </div>

                {/* Progress bar */}
                <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden p-0.5 border border-slate-200/50">
                  <div
                    className={`h-full rounded-full transition-all duration-500 ${
                      isLiquidated
                        ? "bg-gradient-to-r from-emerald-500 to-teal-500"
                        : percentPaid > 0
                        ? "bg-gradient-to-r from-blue-500 to-indigo-600"
                        : "bg-slate-200"
                    }`}
                    style={{ width: `${percentPaid}%` }}
                  />
                </div>

                {/* Pagado vs Balance stats */}
                <div className="flex items-center justify-between text-xs pt-0.5">
                  <div className="flex items-center gap-1 text-slate-600">
                    <span>Cobrado:</span>
                    <span className="font-mono font-bold text-slate-900">
                      {formatMoney(paidConv, currency)}
                    </span>
                    <span className="text-[10px] text-slate-400">({percentPaid}%)</span>
                  </div>

                  <div>
                    {isLiquidated ? (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                        <CheckCircle2 className="h-3 w-3 text-emerald-600" /> Liquidado
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[11px] font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-md">
                        <AlertCircle className="h-3 w-3 text-red-500" />
                        Resta {formatMoney(balanceConv, currency)}
                      </span>
                    )}
                  </div>
                </div>

                {/* Payment Methods & Assigned Terminal */}
                <div className="pt-2 flex flex-wrap items-center gap-1.5">
                  <PaymentBadges
                    methods={
                      c.payment_methods?.length
                        ? c.payment_methods
                        : c.payment_method
                        ? [c.payment_method]
                        : []
                    }
                  />

                  {c.terminal && (
                    <span
                      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-md bg-sky-50 text-sky-800 text-[11px] font-semibold border border-sky-200/80 truncate max-w-full"
                      title={`Cobrado mediante: ${c.terminal}`}
                    >
                      <CreditCard className="w-3.5 h-3.5 text-sky-600 shrink-0" />
                      <span className="truncate">{c.terminal}</span>
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Card Footer: Quick Action Suite */}
            <div className="px-5 py-3 bg-slate-50/80 border-t border-slate-100 flex items-center justify-between gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => onSelectClient?.(c)}
                className="flex-1 text-xs h-8 bg-white hover:bg-slate-100 text-slate-700 font-semibold shadow-2xs border-slate-200 hover:border-slate-300"
              >
                <Eye className="h-3.5 w-3.5 mr-1.5 text-slate-500" /> Ver Ficha
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onReservationClient?.(c)}
                className="flex-1 text-xs h-8 bg-white hover:bg-slate-100 text-slate-700 font-semibold shadow-2xs border-slate-200 hover:border-slate-300"
                title="Gestionar Reserva y Vuelos"
              >
                <Plane className="h-3.5 w-3.5 mr-1.5 text-blue-500" /> Reserva
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => onEditClient?.(c)}
                className="h-8 px-2.5 text-xs bg-white hover:bg-slate-100 text-slate-700 shadow-2xs border-slate-200 hover:border-slate-300"
                title="Editar cliente"
              >
                <Pencil className="h-3.5 w-3.5 text-slate-600" />
              </Button>
            </div>
          </div>
        );
      })}
    </div>
  );
};
